import { useTranslation } from "react-i18next";
import Layout from "@/components/feature/Layout";
import SEO from "@/components/feature/SEO";
import { aboutData } from "@/mocks/about";

export default function AboutPage() {
  const { t } = useTranslation("home");

  return (
    <Layout>
      <SEO
        title="Nosotros | Ventra Core - Expertos en HVAC y Energia UPS en Leon, Guanajuato"
        description="Conoce Ventra Core: mas de 15 anos de experiencia en sistemas HVAC, climatizacion, sistemas electricos especializados y energia UPS en Leon, Guanajuato. Nuestra mision, vision y valores."
        keywords="Ventra Core, empresa HVAC, climatizacion Leon, sistemas electricos, energia UPS, sobre nosotros"
        canonical="https://ventracore.com.mx/nosotros"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": "https://ventracore.com.mx/nosotros#webpage",
            "url": "https://ventracore.com.mx/nosotros",
            "name": "Nosotros | Ventra Core",
            "isPartOf": { "@id": "https://ventracore.com.mx/#website" },
            "description": "Conoce Ventra Core: mas de 15 anos de experiencia en sistemas HVAC y energia UPS en Leon, Guanajuato.",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Inicio",
                "item": "https://ventracore.com.mx/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Nosotros",
                "item": "https://ventracore.com.mx/nosotros",
              },
            ],
          },
        ]}
      />
      <div className="w-full">
        {/* Hero */}
        <section className="relative w-full bg-magenta py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-15">
            <img
              src={aboutData.heroImage}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 uppercase tracking-wide">
              {t("aboutPage.title")}
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
              {t("aboutPage.subtitle")}
            </p>
            <div className="w-20 h-1 bg-white/50 mx-auto mt-6" />
          </div>
        </section>

        {/* Story */}
        <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
              <div className="w-full lg:w-1/2">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={aboutData.companyImage}
                    alt="Equipo Ventra Core"
                    className="w-full h-72 md:h-96 object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-magenta mb-4 uppercase tracking-wide">
                  {t("aboutPage.storyTitle")}
                </h2>
                <p className="text-sm md:text-base text-text-muted leading-relaxed mb-4">
                  {t("aboutPage.storyP1")}
                </p>
                <p className="text-sm md:text-base text-text-muted leading-relaxed mb-4">
                  {t("aboutPage.storyP2")}
                </p>
                <p className="text-sm md:text-base text-text-muted leading-relaxed">
                  {t("aboutPage.storyP3")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-surface">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white rounded-lg p-6 md:p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-brandgreen/10 rounded-md mb-4">
                <span className="w-6 h-6 flex items-center justify-center text-brandgreen">
                  <i className="ri-focus-3-line" />
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-magenta mb-3 uppercase tracking-wide">
                {t("aboutPage.missionTitle")}
              </h3>
              <p className="text-sm md:text-base text-text-muted leading-relaxed">
                {t("aboutPage.missionText")}
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 md:p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-magenta/10 rounded-md mb-4">
                <span className="w-6 h-6 flex items-center justify-center text-magenta">
                  <i className="ri-eye-line" />
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-magenta mb-3 uppercase tracking-wide">
                {t("aboutPage.visionTitle")}
              </h3>
              <p className="text-sm md:text-base text-text-muted leading-relaxed">
                {t("aboutPage.visionText")}
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-magenta uppercase tracking-wide mb-3">
              {t("aboutPage.valuesTitle")}
            </h2>
            <p className="text-sm md:text-base text-text-muted max-w-2xl mx-auto">
              {t("aboutPage.valuesSubtitle")}
            </p>
          </div>
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {aboutData.values.map((value) => (
              <div
                key={value.title}
                className="bg-surface rounded-lg p-5 md:p-6 text-center border border-transparent hover:border-brandgreen/20 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-brandgreen/10 rounded-md mx-auto mb-4">
                  <span className="w-6 h-6 flex items-center justify-center text-brandgreen">
                    <i className={value.icon} />
                  </span>
                </div>
                <h4 className="text-base font-bold text-magenta mb-2">
                  {value.title}
                </h4>
                <p className="text-sm text-text-muted leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-brandgreen">
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 text-center">
            <div>
              <div className="text-3xl md:text-5xl font-bold text-white mb-2">
                15+
              </div>
              <p className="text-sm md:text-base text-white/80 uppercase tracking-wide">
                {t("aboutPage.yearsExperience")}
              </p>
            </div>
            <div>
              <div className="text-3xl md:text-5xl font-bold text-white mb-2">
                800+
              </div>
              <p className="text-sm md:text-base text-white/80 uppercase tracking-wide">
                {t("aboutPage.clientsTrusted")}
              </p>
            </div>
            <div>
              <div className="text-3xl md:text-5xl font-bold text-white mb-2">
                900+
              </div>
              <p className="text-sm md:text-base text-white/80 uppercase tracking-wide">
                {t("aboutPage.projectsDone")}
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-magenta">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 uppercase tracking-wide">
              {t("aboutPage.ctaTitle")}
            </h3>
            <p className="text-sm md:text-base text-white/80 mb-6">
              {t("aboutPage.ctaDesc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+524772847060"
                className="inline-flex items-center gap-2 bg-white text-magenta font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                +52 477 284 70 60
              </a>
              <a
                href="mailto:comercial@ventracore.com.mx"
                className="inline-flex items-center gap-2 bg-white text-magenta font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                comercial@ventracore.com.mx
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}